import type { InviteRecord } from '@dyspatch/core';
import { ChevronRight, KeyRound } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { Empty, EmptyState, GuardedButton } from '@/components/common/bits';
import { SortMenu } from '@/components/common/SortMenu';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Panel, PanelHead } from '@/components/ui/panel';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import type { Verb } from '@/features/actions/verbs';
import { AddPersonButton, AddPersonComposer } from '@/features/admin/AddPersonComposer';
import { AdminShell } from '@/features/admin/AdminShell';
import { EnrollButton, EnrollComposer } from '@/features/fleet/EnrollComposer';
import { useInvites, useUsers } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { useSort } from '@/hooks/use-sort';
import { peopleSortOptions } from '@/lib/admin-sort';
import { api, type PersonRow } from '@/lib/api';
import { useFocusedRow } from '@/lib/focus';
import { clockTime, minutesSince, percent, relativeDeadline, since } from '@/lib/format';
import { applySort, byTime, type SortOption } from '@/lib/sorting';
import { cn } from '@/lib/utils';

/**
 * Why a verb on this row is not available, in the words the person needs.
 *
 * A disabled button with no explanation is the worst of both: it says no and
 * leaves the reason to be guessed. Each of these is also a rule the hub enforces
 * for itself, so the sentence is a preview of the refusal rather than a second
 * opinion about it, and it is carried by `GuardedButton` so that it reaches a
 * keyboard and a screen reader rather than only a hovering mouse.
 */
function blockedReason(
  person: PersonRow,
  verb: 'role' | 'remove' | 'password',
  self: boolean,
  openInvites: number,
): string | null {
  if (self) {
    if (verb === 'role') return 'You cannot change your own role. Ask another admin.';
    if (verb === 'password') return 'Change your own password from your account page.';
    return 'You cannot remove your own account. Ask another admin.';
  }
  if (verb === 'role' || verb === 'password') return null;
  if (person.devices.length) {
    return `${person.name || person.email} still has ${person.devices.length} enrolled device${
      person.devices.length === 1 ? '' : 's'
    } (${person.devices.map((device) => device.name).join(', ')}). Retire those first.`;
  }
  if (openInvites) {
    return `${person.name || person.email} has ${openInvites} open invite${
      openInvites === 1 ? '' : 's'
    }. Revoke those first.`;
  }
  return null;
}

/**
 * What can be done to a person: the row of buttons.
 *
 * The question a button raises is drawn under the row rather than here -- see
 * `confirmFor` on `PeoplePage`, which owns the one ask this whole table can
 * have open. A table row cannot hold a block, and this cell is too narrow for
 * one besides: it sits inside a horizontally scrolling table, so anything
 * drawn absolutely inside it is clipped to a column's width and, below the
 * fold, to a scrollbar nobody notices rather than shown.
 *
 * Editing a limit is still a two-key gesture: the field opens with the current
 * value selected, so typing a number and pressing Enter is the whole
 * interaction. The role toggle and the removal ask before they write, because
 * both change what somebody may do and neither has an undo.
 */
function PersonActions({
  person,
  roleBlocked,
  removeBlocked,
  passwordBlocked,
  onOpen,
}: {
  person: PersonRow;
  roleBlocked: string | null;
  removeBlocked: string | null;
  passwordBlocked: string | null;
  onOpen: (verb: Verb, seed?: string) => void;
}) {
  const roleVerb: Verb = person.isAdmin ? 'demote' : 'promote';

  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      <Button
        size="xs"
        variant="ghost"
        onClick={() => onOpen('limits', String(person.maxConcurrentTasks))}
      >
        Edit limits
      </Button>
      {/* Refused, but readable: `GuardedButton` keeps the button in the tab
          order and points `aria-describedby` at the sentence, because a
          `disabled` button hides its own explanation from everybody who is
          not hovering a mouse over it. */}
      <GuardedButton
        size="xs"
        variant="ghost"
        refusal={roleBlocked}
        onClick={() => onOpen(roleVerb)}
      >
        {person.isAdmin ? 'Remove admin' : 'Make admin'}
      </GuardedButton>
      <GuardedButton
        size="xs"
        variant="ghost"
        refusal={passwordBlocked}
        onClick={() => onOpen('set-password')}
        title="For somebody who has lost their password. The hub cannot email a reset."
      >
        <KeyRound aria-hidden />
        Temporary password
      </GuardedButton>
      <GuardedButton
        size="xs"
        variant="danger"
        refusal={removeBlocked}
        onClick={() => onOpen('remove-member')}
      >
        Remove
      </GuardedButton>
    </div>
  );
}

/**
 * The temporary password, shown once.
 *
 * It replaces the question that produced it, in the same row-wide strip under
 * the same row, so the admin reads it next to the name it is for. There is no
 * second look: the hub does not store it and the journal line does not carry
 * it, which is what the sentence says. Copy is offered because the next thing
 * that happens to this string is a paste into a call or a message.
 */
function TemporaryPasswordReveal({
  person,
  password,
  copied,
  onCopy,
  onDone,
}: {
  person: PersonRow;
  password: string;
  copied: boolean;
  onCopy: () => void;
  onDone: () => void;
}) {
  return (
    <div className="bg-sunken space-y-2 border-t px-gutter py-2.5">
      <p className="text-fg-2 text-xs">
        Give this to {person.name || person.email}. It is shown once and the hub keeps no copy; they
        are signed out everywhere and will be asked to choose their own password when they sign in.
      </p>
      <pre className="bg-card dy-scroll overflow-x-auto rounded-md border px-2.5 py-2 font-mono text-sm select-all">
        {password}
      </pre>
      <div className="flex items-center gap-1.5">
        <Button size="sm" variant="outline" onClick={onCopy}>
          {copied ? 'Copied' : 'Copy password'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}

/**
 * How far back the closed invites go.
 *
 * Long enough to cover "I sent that last week and never heard back", short
 * enough that the section stays a footnote to the open list rather than a second
 * table. An invite lives an hour by default, so fourteen days is a fortnight of
 * enrollments, not a fortnight of noise.
 */
const RECENT_INVITE_DAYS = 14;

/** When an invite stopped being open, which is the moment worth dating. */
function closedAt(invite: InviteRecord): string {
  if (invite.usedAt) return invite.usedAt;
  if (invite.revokedAt) return invite.revokedAt;
  return invite.expiresAt;
}

/**
 * What became of an invite, in the words the admin who minted it is looking for.
 *
 * Only redemption is good news, so only redemption gets a colour; the other two
 * are the same muted badge because "revoked" and "expired" are equally the
 * absence of an enrollment, and a page that tints them differently is asking to
 * be read as if one were worse.
 */
function outcome(invite: InviteRecord): {
  label: string;
  tone: 'success' | 'secondary';
  detail: string;
} {
  if (invite.status === 'used') {
    return {
      label: 'redeemed',
      tone: 'success',
      detail: invite.workerId
        ? `became the device ${invite.workerName}${invite.usedByHostname ? ` on ${invite.usedByHostname}` : ''}`
        : 'became a device that has since been retired',
    };
  }
  if (invite.status === 'revoked') {
    return { label: 'revoked', tone: 'secondary', detail: 'never redeemed' };
  }
  return { label: 'expired', tone: 'secondary', detail: 'nobody redeemed it in time' };
}

/**
 * One invite that is over.
 *
 * No verbs: there is nothing left to do to a code that has been spent, revoked
 * or has run out, and the hub refuses all three. It is a record, so it reads
 * like one -- what it was for, what became of it, who sent it and when it ended.
 */
function ClosedInviteRow({ invite }: { invite: InviteRecord }) {
  const { label, tone, detail } = outcome(invite);
  const at = closedAt(invite);
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 px-gutter py-row text-xs">
      <span className="shrink-0 font-mono text-xs font-medium">{invite.workerName}</span>
      <Badge variant={tone} className="shrink-0">
        {label}
      </Badge>
      <span className="text-fg-3 min-w-0 truncate">for {invite.forEmail}</span>
      <span className="text-fg-3 hidden min-w-0 truncate sm:block">{detail}</span>
      <span className="text-fg-3 hidden shrink-0 md:block">invited by {invite.issuedBy}</span>
      <span className="text-fg-3 tnum ml-auto shrink-0 text-2xs" title={clockTime(at)}>
        {since(minutesSince(at))}
      </span>
    </li>
  );
}

function InviteRow({ invite }: { invite: InviteRecord }) {
  const ask = useAsk();
  const revoke = useHubAction(() => api.revokeInvite(invite.id), {
    onDone: () => ask.close(),
    success: 'Invite revoked.',
    inlineError: true,
  });

  return (
    <li>
      <div className="flex items-center gap-2 px-gutter py-row text-xs">
        <span className="shrink-0 font-mono text-xs font-medium">{invite.workerName}</span>
        <span className="text-fg-3 min-w-0 truncate">for {invite.forEmail}</span>
        <span className="text-fg-3 hidden shrink-0 sm:block">invited by {invite.issuedBy}</span>
        <span
          className="text-fg-3 tnum ml-auto shrink-0 text-2xs"
          title={clockTime(invite.expiresAt)}
        >
          {relativeDeadline(invite.expiresAt)}
        </span>
        <Button size="xs" variant="danger" onClick={() => ask.open('revoke', invite.id)}>
          Revoke
        </Button>
      </div>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={revoke.busy}
          error={revoke.error}
          onCancel={ask.close}
          onConfirm={() => revoke.run()}
        />
      ) : null}
    </li>
  );
}

export function PeoplePage() {
  const { session } = useSession();
  const [enrolling, setEnrolling] = useState(false);
  const [adding, setAdding] = useState(false);
  const users = useUsers();
  const invites = useInvites();

  const people = users.data ?? [];
  const sorting = useSort(peopleSortOptions(), (person) => person.id);
  const sortedPeople = sorting.sort(people);
  const all = invites.data ?? [];
  const open = all.filter((invite) => invite.status === 'open');
  // Everything that closed inside the window, newest first. The list arrives
  // newest-first by `createdAt`, and an invite that was revoked an hour after a
  // later one was minted closed in the other order, so it is sorted on the date
  // this section actually prints.
  //
  // This is a fixed history order, not a reader preference: there is one way
  // this section reads and nothing to reverse it to, so it uses the primitive's
  // comparator and tiebreak directly rather than `useSort`, which would put
  // `sort`/`dir` in the URL for a control this page never draws (issue #74;
  // `SortMenu` already hides itself below two options, and this section has
  // one).
  const cutoff = Date.now() - RECENT_INVITE_DAYS * 24 * 60 * 60_000;
  const recentSort: SortOption<InviteRecord>[] = [
    { value: 'closed', label: 'Closed', compare: byTime(closedAt), natural: 'desc' },
  ];
  const recent = applySort(
    all.filter((invite) => invite.status !== 'open' && Date.parse(closedAt(invite)) >= cutoff),
    recentSort,
    { key: 'closed', direction: 'desc' },
    (invite) => invite.id,
  );
  // The person the palette pointed at, if this page was opened from there.
  const { focus, ref } = useFocusedRow<HTMLTableRowElement>();
  // Which row is the person reading it. The role and removal verbs are refused
  // on yourself, by the hub as well as here, and the button says why.
  const myId = session.user?.id ?? null;

  /**
   * One ask for the whole members table, keyed by person.
   *
   * It lives here rather than in the row for the same reason the devices
   * table keeps its own retire question at the top: the box has to be drawn
   * full width under the row it is about (see `PersonActions`), so one
   * component has to own both the row that raised the question and the row
   * that answers it. It also means only one member's box is open at a time,
   * which is the same rule the row used to keep on its own.
   */
  const ask = useAsk();
  /**
   * The temporary password, held only until the admin closes the box. It comes
   * back in one response and is never journalled, so this is the only place it
   * ever exists; the question box stays open showing it, and "Done" forgets it.
   */
  const [temporary, setTemporary] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const act = useHubAction(
    async (person: PersonRow, verb: Verb, value: string) => {
      if (verb === 'limits') {
        const next = Number.parseInt(value, 10);
        if (Number.isNaN(next)) throw new Error('a whole number of tasks, please');
        return api.setLimits(person.id, next);
      }
      if (verb === 'promote') return api.setRole(person.id, true);
      if (verb === 'demote') return api.setRole(person.id, false);
      if (verb === 'remove-member') return api.removeMember(person.id);
      if (verb === 'set-password') {
        const { password } = await api.setTemporaryPassword(person.id);
        setTemporary(password);
        return password;
      }
      throw new Error(`no person action for ${verb}`);
    },
    // The question box under the row prints the refusal, and these are the
    // verbs the hub refuses most often (the last admin, yourself, a member with
    // an invite still open). The password verb keeps its box open on success,
    // because the box is where the password is shown.
    {
      onDone: () => {
        if (ask.ask?.verb !== 'set-password') ask.close();
      },
      inlineError: true,
    },
  );

  const closeReveal = () => {
    setTemporary(null);
    setCopied(false);
    ask.close();
  };

  // A stale refusal from the last member's box would otherwise sit under the
  // next one's, about a person it was never about.
  const openFor = (person: PersonRow) => (verb: Verb, seed?: string) => {
    act.reset();
    ask.open(verb, person.id, seed);
  };

  const confirmFor = (person: PersonRow) => {
    const open = ask.openOn(person.id);
    if (!open) return null;
    if (open.verb === 'set-password' && temporary) {
      return (
        <TemporaryPasswordReveal
          person={person}
          password={temporary}
          copied={copied}
          onCopy={() => {
            void navigator.clipboard
              ?.writeText(temporary)
              .then(() => setCopied(true))
              // A refused clipboard (no permission, an insecure origin) is not
              // an error to report: the password is on screen to select.
              .catch(() => undefined);
          }}
          onDone={closeReveal}
        />
      );
    }
    // Keyed by verb so a second question opens with an empty field rather
    // than the answer to the first one.
    return (
      <AskBox
        key={open.verb}
        ask={open}
        busy={act.busy}
        error={act.error}
        onCancel={ask.close}
        onConfirm={(value) => act.run(person, open.verb, value)}
      />
    );
  };

  // What a column's header says to a screen reader: the direction it is
  // sorted, or `none` when it is a key the table can sort by but is not the
  // one in use. Every column here has a key (Concurrent limit is `inUse`,
  // Window is `headroom`; see `peopleSortOptions` in `lib/admin-sort.ts` for
  // why they are named for what they are rather than for "spend").
  const ariaSortFor = (key: string): 'ascending' | 'descending' | 'none' =>
    sorting.state.key === key
      ? sorting.state.direction === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none';

  return (
    <AdminShell
      tab="people"
      actions={
        <>
          <AddPersonButton onClick={() => setAdding(true)} />
          <EnrollButton onClick={() => setEnrolling(true)} />
        </>
      }
    >
      <AddPersonComposer open={adding} onClose={() => setAdding(false)} />
      <EnrollComposer open={enrolling} onClose={() => setEnrolling(false)} />

      <Panel>
        <PanelHead
          title="Members"
          meta={`${people.length}`}
          actions={
            <SortMenu options={sorting.options} state={sorting.state} onChange={sorting.choose} />
          }
        />
        {users.isPending && !people.length ? (
          <Empty>Loading people…</Empty>
        ) : !people.length ? (
          <EmptyState
            title="No members yet"
            hint="Add someone to give them an account they can sign in with, or enroll a device under their email."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead aria-sort={ariaSortFor('name')}>Member</TableHead>
                <TableHead aria-sort={ariaSortFor('role')}>Role</TableHead>
                <TableHead aria-sort={ariaSortFor('inUse')}>Concurrent limit</TableHead>
                <TableHead aria-sort={ariaSortFor('headroom')}>Window</TableHead>
                <TableHead aria-sort={ariaSortFor('devices')}>Devices</TableHead>
                <TableHead aria-sort={ariaSortFor('seen')}>Last seen</TableHead>
                <TableHead className="sr-only">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedPeople.map((person) => {
                const self = person.id === myId;
                const openInvites = open.filter(
                  (invite) => invite.forEmail === person.email,
                ).length;
                const confirming = confirmFor(person);
                return (
                  <Fragment key={person.id}>
                    <TableRow
                      ref={person.id === focus ? ref : undefined}
                      className={cn(person.id === focus && 'bg-sel')}
                    >
                      <TableCell>
                        <div className="text-xs font-medium">{person.name || person.email}</div>
                        {person.name ? (
                          <div className="text-fg-3 text-2xs">{person.email}</div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {/* Outline, not the filled accent: the accent is reserved for
                                "a human must act", and a role is an attribute. */}
                        <Badge
                          variant={person.isAdmin ? 'outline' : 'secondary'}
                          className={person.isAdmin ? 'text-foreground font-semibold' : undefined}
                        >
                          {person.isAdmin ? 'admin' : 'member'}
                        </Badge>
                      </TableCell>
                      <TableCell className="tnum text-fg-2 text-xs">
                        {person.account
                          ? `${person.account.load} / ${person.maxConcurrentTasks} in use`
                          : person.maxConcurrentTasks}
                      </TableCell>
                      <TableCell className="tnum text-fg-2 text-xs">
                        {person.account ? percent(person.account.headroom) : 'no device online'}
                      </TableCell>
                      <TableCell className="text-fg-2 font-mono text-2xs">
                        {person.devices.length
                          ? person.devices.map((device) => device.name).join(', ')
                          : 'none'}
                      </TableCell>
                      <TableCell className="tnum text-fg-3 text-xs">
                        {person.lastSeenAt ? since(minutesSince(person.lastSeenAt)) : 'never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <PersonActions
                          person={person}
                          roleBlocked={blockedReason(person, 'role', self, openInvites)}
                          removeBlocked={blockedReason(person, 'remove', self, openInvites)}
                          passwordBlocked={blockedReason(person, 'password', self, openInvites)}
                          onOpen={openFor(person)}
                        />
                      </TableCell>
                    </TableRow>
                    {/* The question, full width, directly under the member it is
                        about -- see `PersonActions` for why it is not drawn in
                        the actions cell itself. */}
                    {confirming ? (
                      <TableRow>
                        <TableCell className="p-0" colSpan={7}>
                          {confirming}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel>
        <PanelHead title="Open invites" meta={open.length ? `${open.length}` : undefined} />
        {open.length ? (
          <ul className="divide-y">
            {open.map((invite) => (
              <InviteRow key={invite.id} invite={invite} />
            ))}
          </ul>
        ) : (
          <p className="text-fg-3 px-gutter py-4 text-center text-xs">No open invites.</p>
        )}
      </Panel>

      {/**
       * What became of the invites that are no longer open.
       *
       * Collapsed, because on a working hub this is history and the open list
       * above it is the work. It is here at all because without it "did they
       * enroll?" had no answer anywhere in the console: a redeemed invite and
       * one that quietly expired both left the page showing nothing, and
       * those are opposite facts.
       */}
      <Panel>
        <details className="group">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-gutter py-[calc(var(--dy-row-py)+1px)]">
            <ChevronRight
              className="text-fg-3 size-3 shrink-0 transition-transform duration-100 group-open:rotate-90"
              aria-hidden
            />
            <h2 className="text-sm font-medium tracking-[-0.006em]">Recent invites</h2>
            <span className="text-fg-3 tnum text-2xs">
              {recent.length
                ? `${recent.length} closed in the last ${RECENT_INVITE_DAYS} days`
                : `none closed in the last ${RECENT_INVITE_DAYS} days`}
            </span>
          </summary>
          {recent.length ? (
            <ul className="divide-y border-t">
              {recent.map((invite) => (
                <ClosedInviteRow key={invite.id} invite={invite} />
              ))}
            </ul>
          ) : (
            <p className="text-fg-3 border-t px-gutter py-4 text-center text-xs">
              Nothing has been redeemed, revoked or left to expire in the last {RECENT_INVITE_DAYS}{' '}
              days.
            </p>
          )}
        </details>
      </Panel>
    </AdminShell>
  );
}
